import type { MantineColorsTuple } from '@mantine/core';

// Brand grayscale. Shade 6 (#8f8b89, "Mittelgrau") is the design-system primary.
// All shades except 4 + 7 are Figma-authoritative (verified via
// `get_variable_defs` on frames 6497:7596 and 6512:8814 — Hellgrau 10/20/30/40%,
// Mittelgrau, Dunkelgrau, color/secondary).
export const vdzGray: MantineColorsTuple = [
  '#fafafa', // 0  - Hellgrau 10%
  '#f5f5f5', // 1  - Hellgrau 20%
  '#eae9e7', // 2  - Hellgrau 30%
  '#d7d7d7', // 3  - Hellgrau 40% (verified)
  '#c9c5c0', // 4  - interpolated
  '#b9b6b0', // 5  - Hellgrau (verified)
  '#8f8b89', // 6  - Mittelgrau (PRIMARY, verified)
  '#6b6864', // 7  - interpolated
  '#4a4a4a', // 8  - Dunkelgrau (verified)
  '#3a3a3a', // 9  - color/secondary (verified)
];

// Orange accent. Shade 6 (#ff7933) is the verified focus / interaction
// accent (e.g. input focus border), Shade 7 (#e67700) der Akzent des gelben
// Summenbalkens (Label-Schrift + Chip-Hintergrund, siehe SummaryBar) — derselbe
// Ton, den die statusPalette schon als Vordergrund fuer „eingereicht"/„warnung"
// nutzt. Alle uebrigen Shades sind um 6 herum interpoliert und bis zu einer
// Figma-Verifikation als vorlaeufig zu betrachten.
export const vdzOrange: MantineColorsTuple = [
  '#fff5f0', // 0  - very light tint (interpolated)
  '#ffe8db', // 1  - pastel (interpolated)
  '#ffcfb3', // 2  - interpolated
  '#ffb589', // 3  - interpolated
  '#ff9c5f', // 4  - interpolated
  '#ff8a45', // 5  - interpolated
  '#ff7933', // 6  - BASE (verified — input focus accent)
  '#e67700', // 7  - SUMMENAKZENT (verified — Summenbalken highlight)
  '#b54a14', // 8  - interpolated
  '#802f0a', // 9  - interpolated
];

// Warm VDZ accent yellow. Shades 1 and 4 are verified via Figma
// `get_design_context` on Components frame 6497:23003: shade 1 (#fff3bf) is
// the pastel fill (Eingereicht badge BG, Gesamtsumme highlight); shade 4
// (#ffe066) is the solid accent (stripe/border/sidebar count badge).
// Other shades are interpolated around these anchors.
export const vdzYellow: MantineColorsTuple = [
  '#fffae8',
  '#fff3bf', // 1 - PASTELL (verified)
  '#ffec96',
  '#ffe57e',
  '#ffe066', // 4 - SOLID ACCENT (verified — Figma Components 6497:23003 eingereicht border)
  '#f5cf3a',
  '#d9b41a',
  '#b59515',
  '#8a710f',
  '#5e4d0a',
];

// Status-badge palette: each variant is BG + BORDER + FG.
// All 7 main statuses verified via `get_design_context` on Components frame 6497:23003.
// 5 Domain-Aliase (aktiv/inaktiv/ok/warnung/fehler) map onto canonical palettes;
// they're semantically distinct labels with the same visual treatment.
export type StatusVariant =
  | 'entwurf' | 'inBearbeitung' | 'eingereicht' | 'revision'
  | 'inPruefung' | 'abgelehnt' | 'freigegeben'
  | 'aktiv' | 'inaktiv' | 'ok' | 'warnung' | 'fehler';

export const statusPalette: Record<StatusVariant, { bg: string; border: string; fg: string }> = {
  entwurf:        { bg: '#f1f3f5', border: '#dee2e6', fg: '#000000' },
  inBearbeitung:  { bg: '#f3d9fa', border: '#e599f7', fg: '#862e9c' },
  eingereicht:    { bg: '#fff3bf', border: '#ffe066', fg: '#e67700' },
  revision:       { bg: '#ffe8cc', border: '#ffc078', fg: '#d9480f' },
  inPruefung:     { bg: '#d0ebff', border: '#91a7ff', fg: '#1864ab' },
  abgelehnt:      { bg: '#ffe3e3', border: '#ffa8a8', fg: '#c92a2a' },
  freigegeben:    { bg: '#d3f9d8', border: '#8ce99a', fg: '#2b8a3e' },
  aktiv:          { bg: '#d3f9d8', border: '#8ce99a', fg: '#2b8a3e' },
  inaktiv:        { bg: '#f1f3f5', border: '#dee2e6', fg: '#000000' },
  ok:             { bg: '#d3f9d8', border: '#8ce99a', fg: '#2b8a3e' },
  warnung:        { bg: '#fff3bf', border: '#ffe066', fg: '#e67700' },
  fehler:         { bg: '#ffe3e3', border: '#ffa8a8', fg: '#c92a2a' },
};
